<template>
  <section class="sa-compose">
    <div class="sa-compose__head">
      <h3 class="sa-compose__title">{{ title }}</h3>
      <p class="sa-compose__subtitle">{{ formatNumberBR(total) }} empresas no total</p>
    </div>

    <div class="sa-compose__chart" aria-hidden="true">
      <VueApexCharts
        type="donut"
        height="180"
        width="100%"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>

    <ul class="sa-compose__legend" role="list">
      <li v-for="item in legendItems" :key="item.label" class="sa-compose__legend-item">
        <div class="sa-compose__legend-main">
          <span class="sa-compose__swatch" :style="{ background: item.color }"></span>
          <span class="sa-compose__label">{{ item.label }}</span>
          <strong class="sa-compose__value">{{ formatNumberBR(item.value) }}</strong>
          <span class="sa-compose__pct">{{ item.percent }}</span>
        </div>
        <div
          v-if="item.children && item.children.length"
          class="sa-compose__children"
        >
          <div
            v-for="child in item.children"
            :key="child.label"
            class="sa-compose__child"
          >
            <span>{{ child.label }}</span>
            <strong>{{ formatNumberBR(child.value) }}</strong>
          </div>
        </div>
      </li>
    </ul>
  </section>
</template>

<script>
import { formatNumberBR } from '@/utils/superAdmin/formatters';

export default {
  name: 'DashboardBaseComposition',
  props: {
    title: { type: String, default: 'Composição da base' },
    escolar: { type: Number, default: 0 },
    corporativo: { type: Number, default: 0 },
    weflix: { type: Number, default: 0 },
  },
  computed: {
    weeducTotal() {
      return Number(this.escolar || 0) + Number(this.corporativo || 0);
    },
    total() {
      return this.weeducTotal + Number(this.weflix || 0);
    },
    chartSeries() {
      return [
        Number(this.escolar || 0),
        Number(this.corporativo || 0),
        Number(this.weflix || 0),
      ];
    },
    chartOptions() {
      return {
        chart: {
          type: 'donut',
          fontFamily: 'Inter, system-ui, sans-serif',
          animations: { enabled: true, speed: 400 },
        },
        labels: ['Escolar', 'Corporativo', 'Weflix'],
        colors: ['#250891', '#7C5CFC', '#1AC7D0'],
        legend: { show: false },
        dataLabels: { enabled: false },
        stroke: { width: 2, colors: ['#fff'] },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#697386',
                  offsetY: 18,
                },
                value: {
                  show: true,
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#191c24',
                  offsetY: -8,
                  formatter: (val) => formatNumberBR(val),
                },
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#697386',
                  formatter: () => formatNumberBR(this.total),
                },
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: (val) => `${formatNumberBR(val)} empresas`,
          },
        },
      };
    },
    legendItems() {
      return [
        {
          label: 'Weeduc',
          value: this.weeducTotal,
          percent: this.toPercent(this.weeducTotal),
          color: '#250891',
          children: [
            { label: 'Escolar', value: this.escolar },
            { label: 'Corporativo', value: this.corporativo },
          ],
        },
        {
          label: 'Weflix',
          value: this.weflix,
          percent: this.toPercent(this.weflix),
          color: '#1AC7D0',
          children: [],
        },
      ];
    },
  },
  methods: {
    formatNumberBR,
    toPercent(value) {
      if (!this.total) return '0%';
      const pct = (Number(value) / this.total) * 100;
      return `${pct.toLocaleString('pt-BR', {
        maximumFractionDigits: 1,
        minimumFractionDigits: pct % 1 === 0 ? 0 : 1,
      })}%`;
    },
  },
};
</script>

<style scoped>
.sa-compose {
  background: var(--dashboard-card, #fff);
  border: 1px solid var(--dashboard-border, #e8eaf0);
  border-radius: 14px;
  padding: 1.15rem 1.2rem 1.25rem;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sa-compose__head {
  margin-bottom: 0.35rem;
}

.sa-compose__title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--dashboard-text, #191c24);
}

.sa-compose__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: var(--dashboard-muted, #697386);
}

.sa-compose__chart {
  width: 100%;
  margin: 0.25rem 0 0.5rem;
}

.sa-compose__legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-top: auto;
}

.sa-compose__legend-item + .sa-compose__legend-item {
  padding-top: 0.85rem;
  border-top: 1px solid var(--dashboard-border, #e8eaf0);
}

.sa-compose__legend-main {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 0.45rem 0.55rem;
}

.sa-compose__swatch {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
}

.sa-compose__label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--dashboard-text, #191c24);
}

.sa-compose__value {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--dashboard-text, #191c24);
  font-variant-numeric: tabular-nums;
}

.sa-compose__pct {
  font-size: 0.75rem;
  color: var(--dashboard-muted, #697386);
  min-width: 2.75rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.sa-compose__children {
  margin: 0.45rem 0 0 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.sa-compose__child {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.8125rem;
  color: var(--dashboard-muted, #697386);
}

.sa-compose__child strong {
  color: var(--dashboard-text, #191c24);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
</style>
